import { useState } from "react";
import * as React from "react";
import { useAiDashboard } from "@/hooks/queries/useAiDashboard";
import { useAiPricing, type PricingRow } from "@/hooks/queries/useAiPricing";
import { toast } from "sonner";

// Thresholds for success rate colour coding
const SUCCESS_RATE = { green: 95, amber: 90 };

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function successRateColor(rate: number): string {
  if (rate >= SUCCESS_RATE.green) return "text-green-600";
  if (rate >= SUCCESS_RATE.amber) return "text-amber-500";
  return "text-red-500";
}

function formatCost(usd: number | null | undefined): string {
  if (usd == null) return "$0.000000";
  return `$${usd.toFixed(6)}`;
}

function formatMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function AiDashboard() {
  const { data, isLoading, isError, error, refetch } = useAiDashboard();
  const {
    data: pricing,
    savePricingRow,
    savingId,
  } = useAiPricing();

  // Pricing editor state
  const [pricingEdits, setPricingEdits] = useState<Record<string, { input: string; output: string }>>({});
  const [pricingOpen, setPricingOpen] = useState(false);

  // Build edit state from pricing data
  React.useEffect(() => {
    if (pricing) {
      const edits: Record<string, { input: string; output: string }> = {};
      for (const row of pricing) {
        edits[row.id] = {
          input: String(row.input_price_per_1k_tokens),
          output: String(row.output_price_per_1k_tokens),
        };
      }
      setPricingEdits(edits);
    }
  }, [pricing]);

  async function handleSavePricingRow(row: PricingRow) {
    const edit = pricingEdits[row.id];
    if (!edit) return;
    const inputVal = parseFloat(edit.input);
    const outputVal = parseFloat(edit.output);
    if (isNaN(inputVal) || isNaN(outputVal)) {
      toast.error("Invalid price values — must be numbers");
      return;
    }
    try {
      await savePricingRow(row, inputVal, outputVal);
      toast.success(`Updated pricing for ${row.model}`);
    } catch (err) {
      toast.error(
        `Failed to save: ${err instanceof Error ? err.message : "Unknown error"}`
      );
    }
  }

  function formatLastUpdated(date: Date): string {
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return "just now";
    if (diffMin === 1) return "1 minute ago";
    return `${diffMin} minutes ago`;
  }

  // Sorted entries for cost tables
  const costByModel = data?.estimated_cost_by_model
    ? Object.entries(data.estimated_cost_by_model).sort((a, b) => b[1] - a[1])
    : [];
  const totalCost30d = costByModel.reduce((s, [, v]) => s + v, 0);

  const costByPrompt = data?.cost_by_prompt
    ? Object.entries(data.cost_by_prompt).sort((a, b) => b[1] - a[1])
    : [];

  const avgDurations = data?.avg_duration_by_function
    ? Object.entries(data.avg_duration_by_function).sort((a, b) => b[1] - a[1])
    : [];

  const recentFailures = data?.recent_failures ?? [];

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">AI Costs</h1>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {isLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Error */}
      {isError && error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">Failed to load</p>
          <p className="text-xs text-red-500 mt-0.5">{error.message}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && !data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-200 p-5 h-24 animate-pulse"
              >
                <div className="h-2 bg-gray-100 rounded w-16 mb-3" />
                <div className="h-8 bg-gray-100 rounded w-12" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data */}
      {data && (
        <div className="space-y-6">
          {/* Row 1 — headline numbers */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label="Total calls" value={data.total_calls} sub="all time" />
            <StatCard label="Calls today" value={data.calls_today} />
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Success rate
              </p>
              <p className={`text-3xl font-bold ${successRateColor(data.success_rate ?? 0)}`}>
                {data.success_rate ?? "—"}%
              </p>
              <p className="text-xs text-gray-400 mt-1">last 7 days</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Est. cost today
              </p>
              <p className="text-3xl font-bold text-gray-900">
                {formatCost(data.estimated_cost_today)}
              </p>
              <p className="text-xs text-gray-400 mt-1">USD, approximate</p>
            </div>
          </div>

          {/* Row 2 — cost by model */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Cost by model — last 30 days
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Provider</th>
                    <th>Calls</th>
                    <th>Total tokens</th>
                    <th>Est. cost</th>
                    <th>% of total</th>
                  </tr>
                </thead>
                <tbody>
                  {costByModel.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-gray-400 text-center py-6">
                        No data yet
                      </td>
                    </tr>
                  ) : (
                    costByModel.map(([model, cost]) => {
                      const stats = data.tokens_by_model?.[model];
                      return (
                        <tr key={model}>
                          <td className="font-mono text-gray-700 text-xs">{model}</td>
                          <td className="text-gray-500 text-xs capitalize">
                            {pricing.find((p) => p.model === model)?.provider ?? "—"}
                          </td>
                          <td className="text-gray-900 font-medium">
                            {stats?.total_calls ?? "—"}
                          </td>
                          <td className="text-gray-500">
                            {stats ? (stats.total_tokens).toLocaleString() : "—"}
                          </td>
                          <td className="text-gray-900 font-medium">{formatCost(cost)}</td>
                          <td className="text-gray-500">
                            {totalCost30d === 0
                              ? "0%"
                              : `${Math.round((cost / totalCost30d) * 100)}%`}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              <div className="px-4 py-2 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Prices configurable in the pricing panel below.
                </p>
              </div>
            </div>
          </div>

          {/* Row 3 — cost by prompt */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Cost by prompt — last 30 days
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Prompt slug</th>
                    <th>Est. cost</th>
                    <th>% of total</th>
                    <th style={{ width: "35%" }}>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {costByPrompt.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-gray-400 text-center py-6">
                        No data yet
                      </td>
                    </tr>
                  ) : (
                    costByPrompt.map(([slug, cost]) => {
                      const pctNum = totalCost30d === 0 ? 0 : Math.round((cost / totalCost30d) * 100);
                      return (
                        <tr key={slug}>
                          <td className="font-mono text-gray-700 text-xs">{slug}</td>
                          <td className="text-gray-900 font-medium">{formatCost(cost)}</td>
                          <td className="text-gray-500">{pctNum}%</td>
                          <td>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-orange-400 rounded-full"
                                style={{ width: `${pctNum}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Row 4 — avg processing time */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Average processing time — last 30 days
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Function</th>
                    <th>Avg duration</th>
                    <th style={{ width: "40%" }}>Bar</th>
                  </tr>
                </thead>
                <tbody>
                  {avgDurations.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-gray-400 text-center py-6">
                        No data yet
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      const maxMs = avgDurations[0]?.[1] ?? 1;
                      return avgDurations.map(([fn, ms]) => (
                        <tr key={fn}>
                          <td className="font-mono text-gray-700 text-xs">{fn}</td>
                          <td className="text-gray-900 font-medium">{formatMs(ms)}</td>
                          <td>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-orange-400 rounded-full"
                                style={{ width: `${Math.round((ms / maxMs) * 100)}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      ));
                    })()
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Row 5 — recent failures */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Recent failures — last 7 days
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {recentFailures.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  No failures in the last 7 days 🟢
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Function</th>
                      <th>Model</th>
                      <th>Error</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentFailures.map((f, i) => (
                      <tr key={i}>
                        <td className="font-mono text-gray-700 text-xs">{f.function_name}</td>
                        <td className="font-mono text-gray-500 text-xs">{f.model}</td>
                        <td className="text-gray-600 text-xs max-w-xs truncate">
                          {f.error_message ?? "—"}
                        </td>
                        <td className="text-gray-400 text-xs">{timeAgo(f.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pricing editor */}
          <div>
            <button
              onClick={() => setPricingOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors mb-3"
            >
              <span>{pricingOpen ? "▼" : "▶"}</span>
              Pricing configuration
            </button>

            {pricingOpen && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-amber-50">
                  <p className="text-xs text-amber-700">
                    Changes affect cost estimates immediately. Prices are approximate —
                    verify with provider docs before making decisions.
                  </p>
                </div>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th>Provider</th>
                      <th>Input $/1k tokens</th>
                      <th>Output $/1k tokens</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricing.map((row) => {
                      const edit = pricingEdits[row.id];
                      if (!edit) return null;
                      return (
                        <tr key={row.id}>
                          <td className="font-mono text-gray-700 text-xs">{row.model}</td>
                          <td className="text-gray-500 text-xs capitalize">{row.provider}</td>
                          <td>
                            <input
                              type="number"
                              step="0.000001"
                              value={edit.input}
                              onChange={(e) =>
                                setPricingEdits((prev) => ({
                                  ...prev,
                                  [row.id]: { ...prev[row.id], input: e.target.value },
                                }))
                              }
                              className="admin-input font-mono text-xs w-32"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.000001"
                              value={edit.output}
                              onChange={(e) =>
                                setPricingEdits((prev) => ({
                                  ...prev,
                                  [row.id]: { ...prev[row.id], output: e.target.value },
                                }))
                              }
                              className="admin-input font-mono text-xs w-32"
                            />
                          </td>
                          <td className="text-gray-400 text-xs max-w-xs truncate">
                            {row.notes ?? "—"}
                          </td>
                          <td>
                            <button
                              onClick={() => handleSavePricingRow(row)}
                              disabled={savingId !== null}
                              className="px-2.5 py-1 text-xs font-medium rounded border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50 transition-colors"
                            >
                              {savingId !== null ? "Saving..." : "Save"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
